import React, { useEffect, useRef, useState } from 'react';
import Drawer from 'react-modern-drawer';
import { useAuth } from '../../../../modules/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import db from '../../../../../services/firebase';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';
import DrawerHeader from '../drawer-components/DrawerHeader';
import { DrawerContent } from '../drawer-components/DrawerContent';
import Overlay from 'app/components/Overlay';
import DrawerFooter from '../drawer-components/DrawerFooter';
import InfoCard from '../drawer-components/InfoCard';
import { AddDrawerIntegrationProps } from '../utils/types';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_WORKSPACE_CLIENT_ID || '';
const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_WORKSPACE_REDIRECT_URI || 
  (window.location.origin.includes('localhost') 
    ? 'http://localhost:3011/integrations' 
    : `${window.location.origin}/integrations`);
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/admin.directory.user.readonly',
  'https://www.googleapis.com/auth/admin.directory.user.security', // Required for fetching user tokens/connected apps
  'https://www.googleapis.com/auth/admin.directory.group.readonly',
  'https://www.googleapis.com/auth/admin.directory.orgunit.readonly',
  'https://www.googleapis.com/auth/admin.directory.domain.readonly',
  'https://www.googleapis.com/auth/directory.readonly',
  'https://www.googleapis.com/auth/contacts.readonly',
].join(' ');

const GoogleWorkspaceDrawer: React.FC<AddDrawerIntegrationProps> = ({ id, isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(false);
  const [openAccordion2, setOpenAccordion2] = useState(false);
  const { currentUser } = useAuth();
  const [showAlert, setShowAlert] = useState(false);
  const drawerTopRef = useRef<HTMLDivElement>(null);
  const [integration, setIntegration] = useState<any[]>([]);
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected';

  const [accessTokenGoogleWorkspace, setAccessTokenGoogleWorkspace] = useState<string | null>(
    localStorage.getItem('google_workspace_access_token')
  );
console.log(accessTokenGoogleWorkspace);
  const getIntegration = React.useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || '';
      if (!id) return;
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id);
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          if (data.name === 'Google Workspace') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
            };
            setIntegration([integrationData]);
            setAccessTokenGoogleWorkspace(data.accessToken);
          }
        }
      });
      return unsubscribe;
    } catch (error) {
      console.error('Error getting integration:', error);
    }
  }, [id, currentUser?.tenantId]);

  useEffect(() => {
    if (isOpen && id) {
      getIntegration();
    }
  }, [id, isOpen, getIntegration]);


  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showAlert]);

  const handleConnect = async () => {
    if (!accessTokenGoogleWorkspace) {
      // Validate required configuration
      if (!GOOGLE_CLIENT_ID) {
        showErrorToast({ message: 'Google Workspace Client ID is not configured. Please contact your administrator.' });
        return;
      }

      if (!GOOGLE_REDIRECT_URI) {
        showErrorToast({ message: 'Google Workspace Redirect URI is not configured. Please contact your administrator.' });
        return;
      }
      setIsLoading(true);
      localStorage.setItem('pendingIntegration', 'Google Workspace');
      const encodedRedirectUri = encodeURIComponent(GOOGLE_REDIRECT_URI);
      const authUrl = `${GOOGLE_AUTH_URL}?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=${encodeURIComponent(GOOGLE_SCOPES)}&access_type=offline&prompt=consent`;
      window.location.href = authUrl;
      return;
    }
  };

  const handleClose = () => {
    setShowAlert(false);
    onClose();
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      const tenantId = currentUser?.tenantId || '';
      if (!id || !tenantId) return;
      const tenantRef = doc(db, 'tenants', tenantId);

      const assetsRef = collection(tenantRef, 'assets');
      const googleWorkspaceAssetsQuery = query(assetsRef, where('platform', '==', 'Google Workspace'));
      const googleWorkspaceAssetsSnapshot = await getDocs(googleWorkspaceAssetsQuery);

      const deleteAssetPromises = googleWorkspaceAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id));
      });

      await Promise.all(deleteAssetPromises);

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id);
      await deleteDoc(integrationRef);

      const vendorsRef = collection(tenantRef, 'vendors');
      const googleWorkspaceVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Google Workspace'));
      const googleWorkspaceVendorSnapshot = await getDocs(googleWorkspaceVendorQuery);

      const deleteVendorPromises = googleWorkspaceVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id));
      });

      await Promise.all(deleteVendorPromises);

      localStorage.removeItem('google_workspace_access_token');
      localStorage.removeItem('google_workspace_refresh_token');

      const activitiesRef = collection(tenantRef, 'activities');
      const currentDate = new Date();
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`;
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Google Workspace integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      });

      showSuccessToast({ message: 'Google Workspace integration disconnected' });

      setIntegration([]);
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error disconnecting Google Workspace:', errorMessage);
      showErrorToast({ message: 'Failed to disconnect Google Workspace' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} direction="right" className="" size="45%">
      <DrawerHeader title="Google Workspace" onClose={onClose} />

      <div
        className="scroll-y overflow-auto p-6 mb-10"
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className="form">
          <DrawerContent
            title="Google Workspace"
            description="Business Suite and Collaboration Platform"
            logo="/integrations/google GSuite.svg"
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo="SecureSlate will be granted permissions to read user, group, organizational unit, and domain data via Google's OAuth 2.0, respecting user permissions."
            accessTypeinfo="Requires an active Google Workspace integration with sufficient permissions to access directory data."
          />

          <>
            <div className="mb-8 d-flex justify-content-between align-items-center">
              <div className="fs-6 fw-bold text-bold">
                {isConnected ? 'Google Workspace Connected to SecureSlate' : 'Connect Google Workspace to SecureSlate'}{' '}
                <Overlay
                  content="Upon successful connection, all users, groups, organizational units, and domains within your Google Workspace account will be automatically imported and presented within the Asset Repository section of the platform for your review and management."
                />
              </div>
            </div>
            <InfoCard
              type="info"
              title="Connect Google Workspace to SecureSlate"
              description="Click on the 'Connect' button below. You will be redirected to Google to authenticate via OAuth and authorize SecureSlate. Once authorized, workspace data will be automatically fetched and configured. Note: Domain-wide delegation must be configured in your Google Workspace admin console for full functionality."
            />
          </>
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  );
};

export default GoogleWorkspaceDrawer;

